/**
 * The taxonomy of the AI Reels clip bank (spec §8.1). Every bank clip carries one value per axis,
 * an energy from 1 to 5 and free tags. The gap planner prompt, the Gemini scoring rubric and the
 * director catalogue use these values. The file has no imports, so every project can use it.
 */

export const BANK_GENRES = ['pop', 'hip-hop', 'electronic', 'rock', 'rnb', 'latin', 'indie', 'acoustic'] as const;
export const BANK_MOODS = ['euphoric', 'melancholic', 'aggressive', 'chill', 'romantic', 'mysterious'] as const;
export const BANK_SETTINGS = [
  'city-night',
  'nature',
  'studio',
  'abstract',
  'road',
  'interior',
  'water',
  'sky',
] as const;
export const BANK_SHOT_TYPES = ['wide', 'medium', 'macro', 'aerial', 'tracking', 'slow-motion'] as const;
export const BANK_MOTIONS = ['static', 'slow', 'medium', 'fast'] as const;
/**
 * `people`: one person or a few people who do not perform. `performer`: a musician, singer or
 * dancer in action, close-ups included. `crowd`: many people, for example a concert audience.
 */
export const BANK_SUBJECTS = ['no-people', 'people', 'performer', 'crowd'] as const;

export type BankGenre = (typeof BANK_GENRES)[number];
export type BankMood = (typeof BANK_MOODS)[number];
export type BankSetting = (typeof BANK_SETTINGS)[number];
export type BankShotType = (typeof BANK_SHOT_TYPES)[number];
export type BankMotion = (typeof BANK_MOTIONS)[number];
export type BankSubject = (typeof BANK_SUBJECTS)[number];

/** The lowest and the highest clip energy. */
export const BANK_ENERGY_MIN = 1;
export const BANK_ENERGY_MAX = 5;

/** All axes in one object (spec §6.6 `bankTaxonomy`). */
export const bankTaxonomy = {
  genres: BANK_GENRES,
  moods: BANK_MOODS,
  settings: BANK_SETTINGS,
  shotTypes: BANK_SHOT_TYPES,
  motions: BANK_MOTIONS,
  subjects: BANK_SUBJECTS,
} as const;

/** Share of the Music24 catalogue per genre; the planner raises the lowest weighted counts first. */
export const GENRE_WEIGHTS: Record<BankGenre, number> = {
  pop: 0.22,
  'hip-hop': 0.2,
  electronic: 0.16,
  rock: 0.1,
  rnb: 0.1,
  latin: 0.08,
  indie: 0.08,
  acoustic: 0.06,
};

/**
 * The target share of published clips per subject (spec §8.1). Owner decision 2026-10-03: at least
 * 60 % of the clips show no people (`no-people` and `crowd`), at most 40 % show `people` or
 * `performer`. A face in a clip is the main cause of a rejected clip.
 */
export const SUBJECT_WEIGHTS: Record<BankSubject, number> = {
  'no-people': 0.5,
  people: 0.15,
  performer: 0.25,
  crowd: 0.1,
};

function isOneOf<T extends string>(values: readonly T[], value: string): value is T {
  return (values as readonly string[]).includes(value);
}

export const isBankGenre = (value: string): value is BankGenre => isOneOf(BANK_GENRES, value);
export const isBankMood = (value: string): value is BankMood => isOneOf(BANK_MOODS, value);
export const isBankSetting = (value: string): value is BankSetting => isOneOf(BANK_SETTINGS, value);
export const isBankShotType = (value: string): value is BankShotType => isOneOf(BANK_SHOT_TYPES, value);
export const isBankMotion = (value: string): value is BankMotion => isOneOf(BANK_MOTIONS, value);
export const isBankSubject = (value: string): value is BankSubject => isOneOf(BANK_SUBJECTS, value);
